import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Alert,
  Image,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import Icon from 'react-native-vector-icons/Feather';
import LinearGradient from 'react-native-linear-gradient';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useNavigation, useRoute, type RouteProp } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import TonyteBackground from '../components/TonyteBackground';
import PickerField from '../components/PickerField';
import PromotionsSection from '../components/PromotionsSection';
import { auth, uploadPromotionCover } from '../firebase';
import { useFeed } from '../hooks/useFeed';
import { useProfile } from '../hooks/useProfile';
import {
  deleteAdminPromotion,
  deleteUnusedPromotionCover,
  fetchEventsByIds,
  getAdminPromotion,
  newPromotionId,
  publishAdminPromotion,
  saveAdminDraft,
  setAdminPromotionArchived,
  unpublishAdminPromotion,
} from '../services/promotionAdmin';
import { Typography } from '../theme';
import { useTheme } from '../theme/useTheme';
import type { TonyteTheme } from '../theme/themes';
import { useI18n } from '../i18n';
import type { RootStackParamList, TonyteActivity } from '../types';
import { isEventActivity } from '../utils/activityKind';
import { parseActivityDate } from '../utils/activityDates';
import { SUPPORTED_CITIES } from '../utils/cities';
import { pickImageFromLibrary } from '../utils/permissions';
import {
  PROMOTION_LIMITS,
  berlinDateString,
  berlinDateTimeToMillis,
  berlinTimeString,
  classifyPromotionEvent,
  computePromotionStatus,
  emptyPromotionDraft,
  formatBerlin,
  hasUnpublishedChanges,
  isEventKnownEnded,
  moveId,
  pruneEndedEvents,
  validatePromotion,
  type PromotionDraft,
  type PublishedSnapshot,
} from '../utils/promotionAdmin';
import type { FeedPromotion } from '../utils/promotions';
import { STATUS_COLORS, STATUS_LABEL_KEYS } from './AdminPromotionsScreen';

const isAdminRole = (role?: string) => role === 'admin' || role === 'co-admin';

// Create / edit / publish a promotion from the app. Edits are saved to the
// draft; users only see what is explicitly published (same model as the
// dashboard — see src/utils/promotionAdmin.ts).
const AdminPromotionEditorScreen: React.FC = () => {
  const { t } = useI18n();
  const { theme } = useTheme();
  const themed = useMemo(() => createThemedStyles(theme), [theme]);
  const insets = useSafeAreaInsets();
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const route = useRoute<RouteProp<RootStackParamList, 'AdminPromotionEditor'>>();
  const existingId = route.params?.promotionId;
  const { profile, loading: profileLoading } = useProfile(auth.currentUser?.uid || null);
  const allowed = isAdminRole(profile?.role);
  const feedOptions = useMemo(() => ({ includeAdminOnlyCities: profile?.role === 'admin' }), [profile?.role]);
  const { activities } = useFeed(feedOptions);

  const idRef = useRef(existingId || newPromotionId());
  const [isNew, setIsNew] = useState(!existingId);
  const [draft, setDraft] = useState<PromotionDraft>(emptyPromotionDraft());
  const [published, setPublished] = useState<PublishedSnapshot | null>(null);
  const [loading, setLoading] = useState(Boolean(existingId));
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const [search, setSearch] = useState('');
  const [startDate, setStartDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endDate, setEndDate] = useState('');
  const [endTime, setEndTime] = useState('');
  const uploadedCovers = useRef<string[]>([]);
  const savedCoverPath = useRef('');

  const applyLoaded = useCallback((next: { draft: PromotionDraft; published: PublishedSnapshot | null }) => {
    setDraft(next.draft);
    setPublished(next.published);
    setStartDate(berlinDateString(next.draft.startAt));
    setStartTime(berlinTimeString(next.draft.startAt));
    setEndDate(berlinDateString(next.draft.endAt));
    setEndTime(berlinTimeString(next.draft.endAt));
    savedCoverPath.current = next.draft.coverImagePath;
    setDirty(false);
  }, []);

  useEffect(() => {
    if (!existingId || !allowed) return;
    getAdminPromotion(existingId)
      .then(result => {
        if (result) applyLoaded(result);
        else setMessage({ kind: 'error', text: t('promotionsAdmin.notFound') });
      })
      .catch(e => setMessage({ kind: 'error', text: String(e?.message || e) }))
      .finally(() => setLoading(false));
  }, [existingId, allowed, applyLoaded, t]);

  const update = useCallback((patch: Partial<PromotionDraft>) => {
    setDraft(current => ({ ...current, ...patch }));
    setDirty(true);
    setMessage(null);
  }, []);

  // Date/time pickers hold Berlin wall-clock strings; the draft holds instants.
  useEffect(() => {
    const startAt = startDate ? berlinDateTimeToMillis(startDate, startTime || '00:00') : null;
    const endAt = endDate ? berlinDateTimeToMillis(endDate, endTime || '23:59') : null;
    setDraft(current => (current.startAt === startAt && current.endAt === endAt ? current : { ...current, startAt, endAt }));
  }, [startDate, startTime, endDate, endTime]);

  const pool = useMemo(() => activities.filter(isEventActivity), [activities]);
  const [extraEvents, setExtraEvents] = useState<TonyteActivity[]>([]);
  const eventsById = useMemo(() => new Map([...extraEvents, ...pool].map(e => [e.id, e])), [pool, extraEvents]);

  // Referenced events not in the upcoming pool (usually ended ones).
  const missingKey = draft.eventIds.filter(id => !pool.some(e => e.id === id) && !extraEvents.some(e => e.id === id)).join(',');
  useEffect(() => {
    if (!missingKey) return;
    let cancelled = false;
    fetchEventsByIds(missingKey.split(',')).then(found => {
      if (!cancelled && found.length) setExtraEvents(current => [...current, ...found.filter(f => !current.some(c => c.id === f.id))]);
    });
    return () => { cancelled = true; };
  }, [missingKey]);
  const validation = useMemo(() => validatePromotion(draft, { intent: 'publish', eventsById }), [draft, eventsById]);
  const now = Date.now();
  const status = isNew ? 'draft' : computePromotionStatus(draft, published, now);
  const live = published?.published === true;
  const scheduling = draft.startAt !== null && draft.startAt > now;
  const changes = live && (dirty || hasUnpublishedChanges(draft, published));

  const results = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return [];
    return pool
      .filter(e => !draft.eventIds.includes(e.id))
      .filter(e => [e.title, e.locationName, e.location, e.startDate, e.city].filter(Boolean).join(' ').toLowerCase().includes(q))
      .slice(0, 15);
  }, [pool, search, draft.eventIds]);

  const preview = useMemo<FeedPromotion[]>(() => {
    const events = draft.eventIds.map(id => eventsById.get(id)).filter((e): e is TonyteActivity => Boolean(e) && classifyPromotionEvent(e).eligible);
    return [{
      promotion: {
        id: 'preview',
        title: draft.title || t('promotionsAdmin.titlePlaceholder'),
        subtitle: draft.subtitle,
        description: draft.description,
        ctaLabel: draft.ctaLabel,
        coverImageUrl: draft.coverImageUrl || null,
        startAt: draft.startAt ?? now,
        endAt: draft.endAt ?? now + 86400000,
        priority: draft.priority,
        cityKeys: draft.cityKeys,
        eventIds: draft.eventIds,
        version: 0,
      },
      events,
    }];
  }, [draft, eventsById, now, t]);

  const pickCover = async () => {
    const result = await pickImageFromLibrary({ mediaType: 'photo', quality: 1 });
    const asset = result?.assets?.[0];
    if (!asset?.uri) return;
    if (asset.fileSize && asset.fileSize > 10 * 1024 * 1024) {
      setMessage({ kind: 'error', text: t('promotionsAdmin.imageTooLarge') });
      return;
    }
    if (asset.type && !/^image\/(jpeg|png|webp|heic|heif)$/.test(asset.type)) {
      setMessage({ kind: 'error', text: t('promotionsAdmin.imageType') });
      return;
    }
    setUploading(true);
    try {
      const { downloadUrl, storagePath } = await uploadPromotionCover(idRef.current, asset.uri);
      uploadedCovers.current.push(storagePath);
      update({ coverImageUrl: downloadUrl, coverImagePath: storagePath });
    } catch (e: any) {
      setMessage({ kind: 'error', text: `${t('promotionsAdmin.uploadFailed')} ${e?.message || ''}` });
    } finally {
      setUploading(false);
    }
  };

  const cleanupCovers = (keep: string[]) => {
    [savedCoverPath.current, ...uploadedCovers.current].forEach(path => { void deleteUnusedPromotionCover(path, keep); });
    uploadedCovers.current = [];
  };

  const run = async (task: () => Promise<void>) => {
    setBusy(true);
    try {
      await task();
    } catch (e: any) {
      setMessage({ kind: 'error', text: String(e?.message || e) });
    } finally {
      setBusy(false);
    }
  };

  const save = async (quiet = false): Promise<boolean> => {
    const { errors } = validatePromotion(draft, { intent: 'save' });
    if (errors.length) {
      setMessage({ kind: 'error', text: errors.join(' ') });
      return false;
    }
    let ok = false;
    // Ended events can never show again — drop them from the list.
    const toSave = { ...draft, eventIds: pruneEndedEvents(draft.eventIds, eventsById) };
    await run(async () => {
      const result = await saveAdminDraft(idRef.current, toSave, isNew);
      setIsNew(false);
      cleanupCovers([result.draft.coverImagePath, result.published?.coverImagePath || ''].filter(Boolean));
      applyLoaded(result);
      if (!quiet) setMessage({ kind: 'ok', text: result.published?.published ? t('promotionsAdmin.savedLive') : t('promotionsAdmin.saved') });
      ok = true;
    });
    return ok;
  };

  const confirm = (title: string, body: string, action: string, destructive = false) => new Promise<boolean>(resolve => {
    Alert.alert(title, body, [
      { text: t('common.cancel'), style: 'cancel', onPress: () => resolve(false) },
      { text: action, style: destructive ? 'destructive' : 'default', onPress: () => resolve(true) },
    ]);
  });

  const publish = async (startNow: boolean) => {
    const candidate = startNow ? { ...draft, startAt: Date.now() } : draft;
    const { errors, warnings } = validatePromotion({ ...candidate, eventIds: pruneEndedEvents(candidate.eventIds, eventsById) }, { intent: 'publish', eventsById });
    if (errors.length) {
      setMessage({ kind: 'error', text: t('promotionsAdmin.fixFirst') });
      return;
    }
    const scheduled = !startNow && (candidate.startAt ?? 0) > Date.now();
    const body = [
      scheduled ? t('promotionsAdmin.confirmSchedule').replace('{date}', formatBerlin(candidate.startAt)) : t('promotionsAdmin.confirmPublish'),
      ...(warnings.length ? ['', ...warnings.map(w => `• ${w}`)] : []),
    ].join('\n');
    if (!(await confirm(scheduled ? t('promotionsAdmin.schedule') : t('promotionsAdmin.publish'), body, scheduled ? t('promotionsAdmin.schedule') : t('promotionsAdmin.publish')))) return;
    const needsPrune = pruneEndedEvents(draft.eventIds, eventsById).length !== draft.eventIds.length;
    if ((dirty || isNew || startNow || needsPrune) && !(await save(true))) return;
    await run(async () => {
      const result = await publishAdminPromotion(idRef.current, { startNow });
      applyLoaded(result);
      setMessage({ kind: 'ok', text: scheduled ? t('promotionsAdmin.scheduled').replace('{date}', formatBerlin(result.published?.startAt ?? null)) : t('promotionsAdmin.published') });
    });
  };

  const unpublish = async () => {
    if (!(await confirm(t('promotionsAdmin.unpublish'), t('promotionsAdmin.confirmUnpublish'), t('promotionsAdmin.unpublish'), true))) return;
    await run(async () => {
      applyLoaded(await unpublishAdminPromotion(idRef.current));
      setMessage({ kind: 'ok', text: t('promotionsAdmin.unpublished') });
    });
  };

  const toggleArchive = async () => {
    const archiving = !draft.archived;
    if (archiving && !(await confirm(t('promotionsAdmin.archive'), t('promotionsAdmin.confirmArchive'), t('promotionsAdmin.archive')))) return;
    if (dirty && !(await save(true))) return;
    await run(async () => {
      applyLoaded(await setAdminPromotionArchived(idRef.current, archiving));
      setMessage({ kind: 'ok', text: archiving ? t('promotionsAdmin.archived') : t('promotionsAdmin.restored') });
    });
  };

  const remove = async () => {
    if (!(await confirm(t('promotionsAdmin.delete'), t('promotionsAdmin.confirmDelete').replace('{title}', draft.title || ''), t('promotionsAdmin.delete'), true))) return;
    await run(async () => {
      await deleteAdminPromotion(idRef.current);
      navigation.goBack();
    });
  };

  const leave = async () => {
    if (dirty && !(await confirm(t('promotionsAdmin.discardTitle'), t('promotionsAdmin.discardBody'), t('promotionsAdmin.discard'), true))) return;
    cleanupCovers([savedCoverPath.current, published?.coverImagePath || ''].filter(Boolean));
    navigation.goBack();
  };

  const statusColors = STATUS_COLORS[status];

  if (!profileLoading && !allowed) {
    return (
      <TonyteBackground appearanceAware style={[styles.container, themed.container, styles.center]}>
        <Icon name="lock" size={28} color={theme.colors.iconMuted} />
        <Text style={[styles.sectionTitle, themed.textPrimary]}>{t('promotionsAdmin.adminsOnly')}</Text>
      </TonyteBackground>
    );
  }

  return (
    <TonyteBackground appearanceAware style={[styles.container, themed.container]}>
      <View style={[styles.header, { paddingTop: insets.top + 10 }]}>
        <TouchableOpacity style={[styles.iconButton, themed.surface]} onPress={leave} accessibilityRole="button" accessibilityLabel={t('common.back')}>
          <Icon name="arrow-left" size={20} color={theme.colors.icon} />
        </TouchableOpacity>
        <View style={styles.headerCopy}>
          <Text style={[styles.headerTitle, themed.textPrimary]} numberOfLines={1}>{isNew ? t('promotionsAdmin.newTitle') : draft.title || t('promotionsAdmin.untitled')}</Text>
          <View style={styles.pills}>
            <View style={[styles.pill, { backgroundColor: statusColors.bg }]}><Text style={[styles.pillText, { color: statusColors.fg }]}>{t(STATUS_LABEL_KEYS[status])}</Text></View>
            {changes ? <View style={[styles.pill, styles.changesPill]}><Text style={[styles.pillText, styles.changesText]}>{t('promotionsAdmin.unpublishedChanges')}</Text></View> : null}
          </View>
        </View>
      </View>

      {loading ? (
        <View style={styles.center}><ActivityIndicator color={theme.colors.accentSecondary} /></View>
      ) : (
        <KeyboardAvoidingView style={styles.container} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
          <ScrollView contentContainerStyle={[styles.content, { paddingBottom: insets.bottom + 40 }]} keyboardShouldPersistTaps="handled">
            {message ? (
              <Text style={[styles.message, message.kind === 'ok' ? styles.messageOk : styles.messageError]} accessibilityLiveRegion="polite">{message.text}</Text>
            ) : null}

            <Text style={[styles.sectionTitle, themed.textPrimary]}>{t('promotionsAdmin.preview')}</Text>
            <PromotionsSection items={preview} desktop={false} onPress={() => {}} />

            <Section title={t('promotionsAdmin.cover')} themed={themed}>
              <View style={styles.cover}>
                <LinearGradient colors={['#1E2A44', '#3A2A4A', '#F27D26']} style={StyleSheet.absoluteFill} />
                {draft.coverImageUrl ? <Image source={{ uri: draft.coverImageUrl }} style={StyleSheet.absoluteFill} resizeMode="cover" /> : null}
                {uploading ? <View style={styles.coverBusy}><ActivityIndicator color="#fff" /></View> : null}
              </View>
              <View style={styles.rowButtons}>
                <SmallButton label={draft.coverImageUrl ? t('promotionsAdmin.replaceImage') : t('promotionsAdmin.chooseImage')} onPress={pickCover} disabled={uploading} themed={themed} />
                {draft.coverImageUrl ? <SmallButton label={t('promotionsAdmin.removeImage')} onPress={() => update({ coverImageUrl: '', coverImagePath: '' })} themed={themed} /> : null}
              </View>
              <Text style={[styles.hint, themed.textMuted]}>{t('promotionsAdmin.coverHint')}</Text>
            </Section>

            <Section title={t('promotionsAdmin.content')} themed={themed}>
              <Field label={`${t('promotionsAdmin.titleLabel')} *`} value={draft.title} max={PROMOTION_LIMITS.title} onChange={v => update({ title: v })} placeholder={t('promotionsAdmin.titlePlaceholder')} themed={themed} />
              <Field label={t('promotionsAdmin.subtitleLabel')} value={draft.subtitle} max={PROMOTION_LIMITS.subtitle} onChange={v => update({ subtitle: v })} themed={themed} />
              <Field label={t('promotionsAdmin.descriptionLabel')} value={draft.description} max={PROMOTION_LIMITS.description} onChange={v => update({ description: v })} multiline themed={themed} />
              <Field label={t('promotionsAdmin.ctaLabel')} value={draft.ctaLabel} max={PROMOTION_LIMITS.ctaLabel} onChange={v => update({ ctaLabel: v })} placeholder={t('promotions.explore')} themed={themed} />
              <Field
                label={t('promotionsAdmin.priorityLabel')}
                value={Number.isFinite(draft.priority) ? String(draft.priority) : ''}
                onChange={v => update({ priority: v === '' ? NaN : Number(v) })}
                keyboardType="number-pad"
                hint={t('promotionsAdmin.priorityHint')}
                themed={themed}
              />
            </Section>

            <Section title={t('promotionsAdmin.schedule')} themed={themed}>
              <Text style={[styles.label, themed.textSecondary]}>{t('promotionsAdmin.starts')} · Europe/Berlin</Text>
              <View style={styles.pickerRow}>
                <View style={styles.flex}><PickerField mode="date" value={startDate} onChange={v => { setStartDate(v); setDirty(true); }} placeholder="YYYY-MM-DD" noMargin /></View>
                <View style={styles.flex}><PickerField mode="time" value={startTime} onChange={v => { setStartTime(v); setDirty(true); }} placeholder="00:00" noMargin /></View>
              </View>
              <Text style={[styles.label, themed.textSecondary]}>{t('promotionsAdmin.ends')} · Europe/Berlin</Text>
              <View style={styles.pickerRow}>
                <View style={styles.flex}><PickerField mode="date" value={endDate} onChange={v => { setEndDate(v); setDirty(true); }} placeholder="YYYY-MM-DD" noMargin /></View>
                <View style={styles.flex}><PickerField mode="time" value={endTime} onChange={v => { setEndTime(v); setDirty(true); }} placeholder="23:59" noMargin /></View>
              </View>
              <Text style={[styles.hint, themed.textMuted]}>{t('promotionsAdmin.scheduleHint')}</Text>
            </Section>

            <Section title={t('promotionsAdmin.cities')} themed={themed}>
              <View style={styles.chips}>
                <Chip label={t('promotionsAdmin.allCities')} active={!draft.cityKeys.length} onPress={() => update({ cityKeys: [] })} themed={themed} />
                {SUPPORTED_CITIES.map(city => (
                  <Chip
                    key={city.value}
                    label={city.label}
                    active={draft.cityKeys.includes(city.value)}
                    onPress={() => update({ cityKeys: draft.cityKeys.includes(city.value) ? draft.cityKeys.filter(c => c !== city.value) : [...draft.cityKeys, city.value] })}
                    themed={themed}
                  />
                ))}
              </View>
            </Section>

            <Section title={`${t('promotionsAdmin.events')} · ${draft.eventIds.length}/${PROMOTION_LIMITS.events}`} themed={themed}>
              {draft.eventIds.length ? draft.eventIds.map((id, index) => {
                const event = eventsById.get(id);
                const verdict = classifyPromotionEvent(event);
                const ended = isEventKnownEnded(event);
                return (
                  <View key={id} style={[styles.eventRow, themed.inner, !verdict.eligible && !ended && styles.eventRowBad, ended && styles.disabled]}>
                    <Text style={[styles.eventIndex, themed.accentText]}>{index + 1}</Text>
                    <View style={styles.flex}>
                      <Text style={[styles.eventTitle, themed.textPrimary]} numberOfLines={1}>{event?.title || id}</Text>
                      <Text style={[styles.eventMeta, themed.textSecondary]} numberOfLines={1}>{event ? eventDateLabel(event) : '—'}</Text>
                      {ended ? <Text style={[styles.eventMeta, themed.textMuted]}>{t('promotionsAdmin.endedAutoRemoved')}</Text>
                        : !verdict.eligible ? <Text style={styles.badText}>{t('promotionsAdmin.wontShow')}: {verdict.reason}</Text> : null}
                    </View>
                    <IconButton name="arrow-up" disabled={index === 0} onPress={() => update({ eventIds: moveId(draft.eventIds, id, -1) })} color={theme.colors.icon} label={t('promotionsAdmin.moveUp')} />
                    <IconButton name="arrow-down" disabled={index === draft.eventIds.length - 1} onPress={() => update({ eventIds: moveId(draft.eventIds, id, 1) })} color={theme.colors.icon} label={t('promotionsAdmin.moveDown')} />
                    <IconButton name="x" onPress={() => update({ eventIds: draft.eventIds.filter(x => x !== id) })} color={theme.colors.icon} label={t('promotionsAdmin.remove')} />
                  </View>
                );
              }) : <Text style={[styles.hint, themed.textMuted]}>{t('promotionsAdmin.noEvents')}</Text>}
              <TextInput
                value={search}
                onChangeText={setSearch}
                placeholder={t('promotionsAdmin.searchEvents')}
                placeholderTextColor={theme.colors.textPlaceholder}
                style={[styles.input, themed.inner, themed.textPrimary]}
                autoCorrect={false}
              />
              {results.length > 1 ? (
                <TouchableOpacity
                  style={[styles.addAll, themed.inner]}
                  onPress={() => update({ eventIds: [...draft.eventIds, ...results.map(e => e.id)].slice(0, PROMOTION_LIMITS.events) })}
                  accessibilityRole="button"
                >
                  <Icon name="plus-circle" size={16} color={theme.colors.accentSecondary} />
                  <Text style={[styles.addAllText, themed.accentText]}>{t('promotionsAdmin.addAll').replace('{count}', String(results.length))}</Text>
                </TouchableOpacity>
              ) : null}
              {results.map(event => (
                <TouchableOpacity
                  key={event.id}
                  style={[styles.eventRow, themed.inner]}
                  disabled={draft.eventIds.length >= PROMOTION_LIMITS.events}
                  onPress={() => update({ eventIds: [...draft.eventIds, event.id].slice(0, PROMOTION_LIMITS.events) })}
                >
                  <View style={styles.flex}>
                    <Text style={[styles.eventTitle, themed.textPrimary]} numberOfLines={1}>{event.title}</Text>
                    <Text style={[styles.eventMeta, themed.textSecondary]} numberOfLines={1}>{eventDateLabel(event)} · {event.locationName || event.location}</Text>
                  </View>
                  <Icon name="plus-circle" size={20} color={theme.colors.accentSecondary} />
                </TouchableOpacity>
              ))}
              {search.trim() && !results.length ? <Text style={[styles.hint, themed.textMuted]}>{t('promotionsAdmin.noMatches')}</Text> : null}
            </Section>

            <Section title={t('promotionsAdmin.checks')} themed={themed}>
              {validation.errors.map(e => <Text key={e} style={styles.badText}>• {e}</Text>)}
              {validation.warnings.map(w => <Text key={w} style={styles.warnText}>• {w}</Text>)}
              {!validation.errors.length && !validation.warnings.length ? <Text style={styles.okText}>{t('promotionsAdmin.ready')}</Text> : null}
              {validation.notes.map(n => <Text key={n} style={[styles.hint, themed.textSecondary]}>ⓘ {n}</Text>)}
            </Section>

            <View style={styles.actions}>
              <ActionButton label={t('promotionsAdmin.saveDraft')} onPress={() => save()} disabled={busy} themed={themed} />
              <ActionButton
                primary
                label={live ? t('promotionsAdmin.publishChanges') : scheduling ? t('promotionsAdmin.schedule') : t('promotionsAdmin.publish')}
                onPress={() => publish(false)}
                disabled={busy}
                themed={themed}
              />
              {scheduling || draft.startAt === null ? <ActionButton label={t('promotionsAdmin.publishNow')} onPress={() => publish(true)} disabled={busy} themed={themed} /> : null}
              {live ? <ActionButton label={t('promotionsAdmin.unpublish')} onPress={unpublish} disabled={busy} themed={themed} /> : null}
              {!isNew ? <ActionButton label={draft.archived ? t('promotionsAdmin.restore') : t('promotionsAdmin.archive')} onPress={toggleArchive} disabled={busy} themed={themed} /> : null}
              {!isNew ? <ActionButton danger label={t('promotionsAdmin.delete')} onPress={remove} disabled={busy} themed={themed} /> : null}
            </View>
            <Text style={[styles.hint, themed.textMuted]}>
              {scheduling ? t('promotionsAdmin.scheduleNote').replace('{date}', formatBerlin(draft.startAt)) : t('promotionsAdmin.publishNote')}
            </Text>
            {live ? <Text style={[styles.hint, themed.textMuted]}>{t('promotionsAdmin.liveVersion').replace('{version}', String(published?.version)).replace('{date}', formatBerlin(published?.publishedAt ?? null))}</Text> : null}
          </ScrollView>
        </KeyboardAvoidingView>
      )}
    </TonyteBackground>
  );
};

function eventDateLabel(event: TonyteActivity) {
  const start = parseActivityDate(event).startAt;
  return start ? formatBerlin(start.getTime()) : '—';
}

type Themed = ReturnType<typeof createThemedStyles>;

const Section: React.FC<{ title: string; themed: Themed; children: React.ReactNode }> = ({ title, themed, children }) => (
  <View style={[styles.section, themed.surface]}>
    <Text style={[styles.sectionTitle, themed.textPrimary]}>{title}</Text>
    {children}
  </View>
);

const Field: React.FC<{
  label: string; value: string; onChange: (v: string) => void; themed: Themed;
  max?: number; placeholder?: string; multiline?: boolean; keyboardType?: 'number-pad'; hint?: string;
}> = ({ label, value, onChange, themed, max, placeholder, multiline, keyboardType, hint }) => {
  const { theme } = useTheme();
  return (
    <View style={styles.field}>
      <View style={styles.labelRow}>
        <Text style={[styles.label, themed.textSecondary]}>{label}</Text>
        {max ? <Text style={[styles.counter, themed.textMuted]}>{value.length}/{max}</Text> : null}
      </View>
      <TextInput
        value={value}
        onChangeText={onChange}
        maxLength={max}
        placeholder={placeholder}
        placeholderTextColor={theme.colors.textPlaceholder}
        multiline={multiline}
        keyboardType={keyboardType}
        style={[styles.input, multiline && styles.inputMultiline, themed.inner, themed.textPrimary]}
      />
      {hint ? <Text style={[styles.hint, themed.textMuted]}>{hint}</Text> : null}
    </View>
  );
};

const Chip: React.FC<{ label: string; active: boolean; onPress: () => void; themed: Themed }> = ({ label, active, onPress, themed }) => (
  <TouchableOpacity onPress={onPress} style={[styles.chip, active ? themed.chipActive : themed.inner]} accessibilityRole="checkbox" accessibilityState={{ checked: active }}>
    <Text style={[styles.chipText, active ? themed.chipActiveText : themed.textSecondary]}>{label}</Text>
  </TouchableOpacity>
);

const SmallButton: React.FC<{ label: string; onPress: () => void; disabled?: boolean; themed: Themed }> = ({ label, onPress, disabled, themed }) => (
  <TouchableOpacity onPress={onPress} disabled={disabled} style={[styles.smallButton, themed.inner, disabled && styles.disabled]}>
    <Text style={[styles.smallButtonText, themed.textPrimary]}>{label}</Text>
  </TouchableOpacity>
);

const IconButton: React.FC<{ name: string; onPress: () => void; disabled?: boolean; color: string; label: string }> = ({ name, onPress, disabled, color, label }) => (
  <TouchableOpacity onPress={onPress} disabled={disabled} style={[styles.iconSmall, disabled && styles.disabled]} accessibilityRole="button" accessibilityLabel={label} hitSlop={6}>
    <Icon name={name} size={16} color={color} />
  </TouchableOpacity>
);

const ActionButton: React.FC<{ label: string; onPress: () => void; disabled?: boolean; primary?: boolean; danger?: boolean; themed: Themed }> = ({ label, onPress, disabled, primary, danger, themed }) => (
  <TouchableOpacity
    onPress={onPress}
    disabled={disabled}
    style={[styles.action, primary ? styles.actionPrimary : themed.surface, disabled && styles.disabled]}
    accessibilityRole="button"
  >
    <Text style={[styles.actionText, primary ? styles.actionPrimaryText : danger ? styles.dangerText : themed.textPrimary]}>{label}</Text>
  </TouchableOpacity>
);

const styles = StyleSheet.create({
  container: { flex: 1 },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center', gap: 10 },
  flex: { flex: 1, minWidth: 0 },
  header: { flexDirection: 'row', alignItems: 'center', gap: 12, paddingHorizontal: 16, paddingBottom: 10 },
  iconButton: { width: 40, height: 40, borderRadius: 14, borderWidth: 1, alignItems: 'center', justifyContent: 'center' },
  headerCopy: { flex: 1, minWidth: 0, gap: 4 },
  headerTitle: { fontSize: 18, fontWeight: Typography.extrabold },
  pills: { flexDirection: 'row', flexWrap: 'wrap', gap: 6 },
  pill: { borderRadius: 999, paddingHorizontal: 8, paddingVertical: 2 },
  pillText: { fontSize: 11, fontWeight: Typography.extrabold },
  changesPill: { backgroundColor: 'rgba(242,125,38,0.14)' },
  changesText: { color: '#E56816' },
  content: { paddingHorizontal: 16, gap: 14 },
  message: { borderRadius: 12, padding: 10, fontSize: 13, fontWeight: Typography.semibold, overflow: 'hidden' },
  messageOk: { backgroundColor: 'rgba(34,197,94,0.14)', color: '#16A34A' },
  messageError: { backgroundColor: 'rgba(239,68,68,0.12)', color: '#EF4444' },
  section: { borderRadius: 18, borderWidth: 1, padding: 14, gap: 10 },
  sectionTitle: { fontSize: 15, fontWeight: Typography.extrabold },
  cover: { width: '100%', aspectRatio: 16 / 9, borderRadius: 14, overflow: 'hidden' },
  coverBusy: { position: 'absolute', top: 0, right: 0, bottom: 0, left: 0, backgroundColor: 'rgba(0,0,0,0.4)', alignItems: 'center', justifyContent: 'center' },
  rowButtons: { flexDirection: 'row', gap: 8, flexWrap: 'wrap' },
  smallButton: { borderRadius: 999, borderWidth: 1, paddingHorizontal: 14, paddingVertical: 8 },
  smallButtonText: { fontSize: 13, fontWeight: Typography.bold },
  field: { gap: 6 },
  labelRow: { flexDirection: 'row', justifyContent: 'space-between' },
  label: { fontSize: 12, fontWeight: Typography.bold },
  counter: { fontSize: 11 },
  input: { borderRadius: 12, borderWidth: 1, paddingHorizontal: 12, paddingVertical: 10, fontSize: 15 },
  inputMultiline: { minHeight: 96, textAlignVertical: 'top' },
  hint: { fontSize: 12, lineHeight: 17 },
  pickerRow: { flexDirection: 'row', gap: 8 },
  chips: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  chip: { borderRadius: 999, borderWidth: 1, paddingHorizontal: 12, paddingVertical: 7 },
  chipText: { fontSize: 13, fontWeight: Typography.bold },
  eventRow: { flexDirection: 'row', alignItems: 'center', gap: 8, borderRadius: 12, borderWidth: 1, padding: 10 },
  eventRowBad: { borderColor: 'rgba(239,68,68,0.45)' },
  eventIndex: { width: 18, fontSize: 13, fontWeight: Typography.extrabold },
  eventTitle: { fontSize: 14, fontWeight: Typography.bold },
  eventMeta: { fontSize: 12 },
  iconSmall: { width: 30, height: 30, alignItems: 'center', justifyContent: 'center' },
  badText: { color: '#EF4444', fontSize: 12, lineHeight: 17 },
  warnText: { color: '#D97706', fontSize: 12, lineHeight: 17 },
  okText: { color: '#16A34A', fontSize: 13, fontWeight: Typography.bold },
  addAll: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6, borderRadius: 12, borderWidth: 1, paddingVertical: 10 },
  addAllText: { fontSize: 13, fontWeight: Typography.extrabold },
  actions: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  action: { flexGrow: 1, minWidth: '45%', borderRadius: 14, borderWidth: 1, paddingVertical: 13, alignItems: 'center' },
  actionPrimary: { backgroundColor: '#F27D26', borderColor: '#F27D26' },
  actionText: { fontSize: 14, fontWeight: Typography.extrabold },
  actionPrimaryText: { color: '#fff' },
  dangerText: { color: '#EF4444' },
  disabled: { opacity: 0.45 },
});

const createThemedStyles = (theme: TonyteTheme) => StyleSheet.create({
  container: { backgroundColor: theme.colors.background },
  textPrimary: { color: theme.colors.textPrimary },
  textSecondary: { color: theme.colors.textSecondary },
  textMuted: { color: theme.colors.textMuted },
  accentText: { color: theme.colors.accentSecondary },
  surface: { backgroundColor: theme.colors.surfaceElevated, borderColor: theme.colors.border },
  inner: { backgroundColor: theme.colors.inputBackground, borderColor: theme.colors.border },
  chipActive: { backgroundColor: theme.colors.accentSecondary, borderColor: theme.colors.accentSecondary },
  chipActiveText: { color: theme.isDark ? '#04201F' : '#FFFFFF' },
});

export default AdminPromotionEditorScreen;
